import { createContext, useContext, useState, useCallback } from "react";

const ActionBarContext = createContext();

/**
 * ActionBarProvider - Provides action bar state management across the app
 * 
 * Allows any component to set action bar content that will be rendered
 * in the AppHeader's action bar area.
 */
export function ActionBarProvider({ children }) {
  const [actionBarContent, setActionBarContent] = useState(null);

  const setContent = useCallback((content) => {
    setActionBarContent(content);
  }, []);

  const clearContent = useCallback(() => {
    setActionBarContent(null);
  }, []);

  return (
    <ActionBarContext.Provider value={{ actionBarContent, setContent, clearContent }}>
      {children}
    </ActionBarContext.Provider>
  );
}

/**
 * useActionBar - Hook to access and modify action bar content
 * 
 * Usage:
 * const { setContent, clearContent } = useActionBar();
 * 
 * useEffect(() => {
 *   setContent(<MyButtons />);
 *   return () => clearContent();
 * }, []);
 */
export function useActionBar() {
  const context = useContext(ActionBarContext);
  if (!context) {
    throw new Error("useActionBar must be used within an ActionBarProvider");
  }
  return context;
}

export default ActionBarContext;
